import { useCallback, useEffect, useState } from 'react';
import useContent from '../hooks/useContent';
import useMedia from '../hooks/useMedia';
import useSettings from '../hooks/useSettings';

function getTypeLabel(val) {
  if (val === null || val === undefined) return 'Null';
  if (Array.isArray(val)) return `Array[${val.length}]`;
  if (typeof val === 'object') return 'Object';
  if (typeof val === 'number') return 'Number';
  if (typeof val === 'boolean') return 'Boolean';
  return 'String';
}

function TreeNode({ node, selectedPath, onSelect, filterText, depth = 0 }) {
  const [expanded, setExpanded] = useState(depth < 2);

  const hasChildren = node.children && node.children.length > 0;
  const isSelected = selectedPath === node.path;

  // Filter matching
  const matchesFilter = filterText
    ? node.name.toLowerCase().includes(filterText.toLowerCase()) || node.path.toLowerCase().includes(filterText.toLowerCase())
    : true;

  if (!matchesFilter && !hasChildren) return null;

  return (
    <div style={{ marginLeft: `${depth * 14}px` }}>
      <div
        onClick={() => onSelect(node)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.4rem',
          padding: '0.3rem 0.5rem',
          borderRadius: '4px',
          cursor: 'pointer',
          background: isSelected ? '#3b82f6' : 'transparent',
          color: isSelected ? '#ffffff' : '#1e293b',
          fontSize: '0.85rem',
          fontWeight: node.isFolder ? 600 : 400,
          userSelect: 'none',
          marginBottom: '2px',
        }}
      >
        {hasChildren ? (
          <span
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
            style={{
              width: '14px',
              fontSize: '0.75rem',
              display: 'inline-block',
              textAlign: 'center',
              color: isSelected ? '#fff' : '#64748b',
            }}
          >
            {expanded ? '▼' : '▶'}
          </span>
        ) : (
          <span style={{ width: '14px' }} />
        )}
        <span>{node.icon || (node.isFolder ? '📁' : '📄')}</span>
        <span style={{ flexGrow: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {node.name}
        </span>
      </div>

      {hasChildren && expanded && (
        <div>
          {node.children.map((child) => (
            <TreeNode
              key={child.path}
              node={child}
              selectedPath={selectedPath}
              onSelect={onSelect}
              filterText={filterText}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function ContentExplorer() {
  const { listContent } = useContent();
  const { listMedia } = useMedia();
  const settingsApi = useSettings();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterText, setFilterText] = useState('');
  const [treeData, setTreeData] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [copied, setCopied] = useState(false);

  const loadRepository = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [contentRes, mediaRes, settingsRes] = await Promise.all([
        listContent({ limit: 100 }),
        listMedia({ limit: 100 }),
        settingsApi.getSettings(),
      ]);

      const items = contentRes.items || [];
      const mediaItems = mediaRes.items || [];

      const pages = items.filter((i) => i.contentType === 'page');
      const posts = items.filter((i) => i.contentType === 'post');

      const buildItemNode = (item, parentPath) => {
        const path = `${parentPath}/${item.slug || item.contentId}`;
        const blocks = item.body?.blocks || [];

        return {
          path,
          name: item.title || item.slug || item.contentId,
          icon: '📄',
          isFolder: true,
          data: item,
          children: [
            ...blocks.map((block, idx) => ({
              path: `${path}/blocks/${block.id || idx}`,
              name: `Block #${idx + 1} [${block.type}]`,
              icon: '🧱',
              isFolder: false,
              data: block,
              children: [],
            })),
          ],
        };
      };

      const rootNode = {
        path: '/content',
        name: 'content [JCR / Content Fragments]',
        icon: '🗂️',
        isFolder: true,
        data: { totalItems: items.length, totalMedia: mediaItems.length },
        children: [
          {
            path: '/content/pages',
            name: `pages (${pages.length})`,
            icon: '📁',
            isFolder: true,
            data: { count: pages.length },
            children: pages.map((item) => buildItemNode(item, '/content/pages')),
          },
          {
            path: '/content/posts',
            name: `posts (${posts.length})`,
            icon: '📁',
            isFolder: true,
            data: { count: posts.length },
            children: posts.map((item) => buildItemNode(item, '/content/posts')),
          },
          {
            path: '/content/media',
            name: `media (${mediaItems.length})`,
            icon: '📁',
            isFolder: true,
            data: { count: mediaItems.length },
            children: mediaItems.map((m) => ({
              path: `/content/media/${m.mediaId}`,
              name: m.filename || m.objectKey,
              icon: '🖼️',
              isFolder: false,
              data: m,
              children: [],
            })),
          },
          {
            path: '/settings',
            name: 'settings & navigation',
            icon: '⚙️',
            isFolder: true,
            data: settingsRes,
            children: [
              {
                path: '/settings/navigation',
                name: `navigation (${settingsRes.navLinks?.length || 0} links)`,
                icon: '🧭',
                isFolder: false,
                data: settingsRes.navLinks || [],
                children: [],
              },
            ],
          },
        ],
      };

      setTreeData(rootNode);
      setSelectedNode(rootNode);
    } catch (err) {
      setError(err.message || 'Fehler beim Laden des Content-Explorers.');
    } finally {
      setLoading(false);
    }
  }, [listContent, listMedia, settingsApi]);

  useEffect(() => {
    loadRepository();
  }, [loadRepository]);

  const copyJson = () => {
    if (!selectedNode?.data) return;
    navigator.clipboard.writeText(JSON.stringify(selectedNode.data, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const renderPropertiesTable = (data) => {
    if (!data || typeof data !== 'object') {
      return <p className="muted">Keine Eigenschaften vorhanden.</p>;
    }

    const entries = Object.entries(data);

    return (
      <div className="table-wrap">
        <table className="table compact" style={{ fontSize: '0.85rem' }}>
          <thead>
            <tr>
              <th>Eigenschaft (Property)</th>
              <th>Typ</th>
              <th>Wert (Value)</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(([key, value]) => {
              const displayVal = typeof value === 'object' ? JSON.stringify(value) : String(value);
              return (
                <tr key={key}>
                  <td>
                    <strong style={{ color: '#0f172a' }}>{key}</strong>
                  </td>
                  <td>
                    <span className="badge" style={{ fontSize: '0.75rem', background: '#f1f5f9', color: '#475569' }}>
                      {getTypeLabel(value)}
                    </span>
                  </td>
                  <td style={{ wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {displayVal.length > 120 ? `${displayVal.slice(0, 120)}…` : displayVal}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="stack gap-lg">
      <section className="card">
        <div className="row between responsive-gap">
          <div>
            <h2>CRXDE Content Fragment Explorer</h2>
            <p className="muted">
              AEM CRXDE Lite inspirierte Baumansicht zum Lesen von Content-Fragmenten, Strukturblöcken und Metadaten.
            </p>
          </div>
          <button className="button secondary small" onClick={loadRepository} disabled={loading}>
            {loading ? 'Aktualisieren…' : '🔄 Aktualisieren'}
          </button>
        </div>
        {error ? <div className="alert error" style={{ marginTop: '0.75rem' }}>{error}</div> : null}
      </section>

      {/* Explorer Layout: Tree Left, Properties Right */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '320px 1fr',
          gap: '1.25rem',
          minHeight: '600px',
        }}
      >
        {/* Left Pane: Tree View */}
        <div
          className="card"
          style={{
            padding: '1rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
            maxHeight: '750px',
            overflowY: 'auto',
          }}
        >
          <input
            type="text"
            placeholder="Knoten filtern…"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            style={{ fontSize: '0.85rem', padding: '0.4rem 0.6rem' }}
          />

          <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: '#64748b', fontWeight: 600 }}>
            Repository Tree
          </div>

          {loading ? (
            <p className="muted small">Lade Content-Baum…</p>
          ) : treeData ? (
            <TreeNode
              node={treeData}
              selectedPath={selectedNode?.path}
              onSelect={(n) => setSelectedNode(n)}
              filterText={filterText}
            />
          ) : (
            <p className="muted small">Kein Content-Baum verfügbar.</p>
          )}
        </div>

        {/* Right Pane: Node Inspector */}
        <div className="card stack gap-md" style={{ maxHeight: '750px', overflowY: 'auto' }}>
          {selectedNode ? (
            <>
              {/* Header Breadcrumb */}
              <div className="row between align-center" style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '0.75rem' }}>
                <div>
                  <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: '#64748b', fontWeight: 600 }}>
                    Pfad / Node Path
                  </div>
                  <div style={{ fontFamily: 'monospace', fontWeight: 700, color: '#1e293b', fontSize: '1rem', marginTop: '0.2rem' }}>
                    {selectedNode.icon} {selectedNode.path}
                  </div>
                </div>
                <button type="button" className="button ghost small" onClick={copyJson}>
                  {copied ? 'Kopiert ✓' : '📋 JSON kopieren'}
                </button>
              </div>

              {/* Properties Section */}
              <div>
                <h4 style={{ marginBottom: '0.5rem' }}>Eigenschaften (JCR Properties)</h4>
                {renderPropertiesTable(selectedNode.data)}
              </div>

              {/* Raw JSON Fragment Inspector */}
              <div>
                <h4 style={{ marginBottom: '0.5rem' }}>Content Fragment Payload (JSON)</h4>
                <pre
                  style={{
                    background: '#0f172a',
                    color: '#38bdf8',
                    padding: '1rem',
                    borderRadius: '8px',
                    fontSize: '0.8rem',
                    maxHeight: '320px',
                    overflow: 'auto',
                    lineHeight: 1.4,
                  }}
                >
                  {JSON.stringify(selectedNode.data, null, 2)}
                </pre>
              </div>
            </>
          ) : (
            <div className="media-empty">
              <p className="muted">Wähle einen Knoten im linken Content-Baum aus.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
